import { useState } from 'react';
import Icon from '../common/Icon.jsx';
import {
  LOCAL_AI_PREFERENCES,
  LOCAL_AI_TIERS,
  deleteLocalAiModels,
  deleteStandardLocalAiModel,
  describeLocalAiPreference,
  getCachedLocalAiCapability,
  getLocalAiPreference,
  runEnhancedCompatibilityCheck,
  runLocalAiCompatibilityCheck,
  setLocalAiPreference,
} from '../../services/localAiCapabilityService.js';

export default function LocalAiSettings({ onClose }) {
  const [capability, setCapability] = useState(() => getCachedLocalAiCapability());
  const [preference, setPreferenceState] = useState(() => getLocalAiPreference());
  const [testingModel, setTestingModel] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingStandard, setIsDeletingStandard] = useState(false);

  const standardModelDeleted = capability?.standardModelDeleted === true;
  const canUseStandard = !standardModelDeleted && [
    LOCAL_AI_TIERS.standard,
    LOCAL_AI_TIERS.enhancedCandidate,
    LOCAL_AI_TIERS.enhanced,
  ].includes(capability?.tier);
  const canUseEnhanced = capability?.tier === LOCAL_AI_TIERS.enhanced;
  const canTestEnhanced = canUseStandard && !canUseEnhanced;
  const canDeleteStandard = canUseEnhanced && !standardModelDeleted;
  const score = typeof capability?.score === 'number' ? capability.score : null;
  const isTesting = testingModel !== null;

  const runStandardTest = async () => {
    setTestingModel('standard');
    try {
      await waitForPaint();
      const result = await runLocalAiCompatibilityCheck();
      setCapability(result);
    } finally {
      setTestingModel(null);
    }
  };

  const runEnhancedTest = async () => {
    setTestingModel('enhanced');
    try {
      await waitForPaint();
      const result = await runEnhancedCompatibilityCheck();
      setCapability(result);
    } finally {
      setTestingModel(null);
    }
  };

  const choosePreference = (next) => {
    const stored = setLocalAiPreference(next);
    setPreferenceState(stored);
  };

  const deleteModels = async () => {
    setIsDeleting(true);
    try {
      await deleteLocalAiModels();
      setCapability(null);
      setPreferenceState(getLocalAiPreference());
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteStandardModel = async () => {
    setIsDeletingStandard(true);
    try {
      const result = await deleteStandardLocalAiModel();
      setCapability(result);
      setPreferenceState(getLocalAiPreference());
    } finally {
      setIsDeletingStandard(false);
    }
  };

  return (
    <div className="mb-4 rounded-3xl bg-bg-card p-4 ring-1 ring-stroke shadow-card animate-slide-up">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-2xs font-bold uppercase tracking-widest-plus text-f1-red">
            Local AI Compatibility
          </p>
          <h2 className="mt-1 text-base font-bold text-ink-primary">
            {capability?.label || 'Local AI not tested'}
          </h2>
        </div>
        <button
          onClick={onClose}
          aria-label="Close local AI settings"
          className="pressable flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-card2 text-ink-secondary ring-1 ring-stroke hover:text-ink-primary"
        >
          <Icon name="close" size={15} />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-[auto_1fr] gap-3 rounded-2xl bg-bg-card2 p-3 ring-1 ring-stroke/80">
        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${scoreColour(score)}`}>
          <Icon name="bolt" size={20} />
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-2xl font-bold tabular-nums text-ink-primary">
              {score ?? '--'}
            </span>
            <span className="text-xs text-ink-tertiary">/ 100</span>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-ink-secondary">
            {capability?.reason || 'Run a quick benchmark to see whether this phone can run Gemma locally.'}
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <Metric label="Mode" value={describeLocalAiPreference(preference)} />
        <Metric label="Tier" value={formatTier(capability?.tier)} />
        <Metric label="E2B speed" value={formatSpeed(capability?.metrics?.standard?.tokensPerSecond)} />
        <Metric label="E2B first token" value={formatMs(capability?.metrics?.standard?.timeToFirstTokenMs)} />
        <Metric label="E4B speed" value={formatSpeed(capability?.metrics?.enhanced?.tokensPerSecond)} />
        <Metric label="E4B first token" value={formatMs(capability?.metrics?.enhanced?.timeToFirstTokenMs)} />
      </div>

      <div className="mt-4 space-y-2">
        <PreferenceButton
          label="Auto"
          description="Let Oracle choose the best available mode."
          active={preference === LOCAL_AI_PREFERENCES.auto}
          onClick={() => choosePreference(LOCAL_AI_PREFERENCES.auto)}
        />
        <PreferenceButton
          label="Online AI only"
          description="Disable local model use and answer online when connected."
          active={preference === LOCAL_AI_PREFERENCES.onlineOnly}
          onClick={() => choosePreference(LOCAL_AI_PREFERENCES.onlineOnly)}
        />
        <PreferenceButton
          label="Standard Local AI"
          description="Use Gemma 4 E2B when this device passes the benchmark."
          active={preference === LOCAL_AI_PREFERENCES.standard}
          disabled={!canUseStandard}
          onClick={() => choosePreference(LOCAL_AI_PREFERENCES.standard)}
        />
        <PreferenceButton
          label="Enhanced Local AI"
          description="Use Gemma 4 E4B after it passes its own benchmark."
          active={preference === LOCAL_AI_PREFERENCES.enhanced}
          disabled={!canUseEnhanced}
          onClick={() => choosePreference(LOCAL_AI_PREFERENCES.enhanced)}
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={runStandardTest}
          disabled={isTesting || isDeleting || isDeletingStandard}
          className="pressable rounded-full bg-f1-red px-4 py-2 text-xs font-bold text-white shadow-glow-red-soft disabled:opacity-50"
        >
          {testingModel === 'standard' ? 'Testing E2B...' : capability ? 'Retest E2B' : 'Test Local AI'}
        </button>
        {canTestEnhanced ? (
          <button
            onClick={runEnhancedTest}
            disabled={isTesting || isDeleting || isDeletingStandard}
            className="pressable rounded-full bg-bg-card2 px-4 py-2 text-xs font-bold text-ink-primary ring-1 ring-stroke disabled:opacity-50"
          >
            {testingModel === 'enhanced' ? 'Testing E4B...' : 'Test E4B'}
          </button>
        ) : null}
        {canDeleteStandard ? (
          <button
            onClick={deleteStandardModel}
            disabled={isTesting || isDeleting || isDeletingStandard}
            className="pressable rounded-full bg-bg-card2 px-4 py-2 text-xs font-bold text-ink-primary ring-1 ring-stroke disabled:opacity-50"
          >
            {isDeletingStandard ? 'Deleting E2B...' : 'Delete E2B'}
          </button>
        ) : null}
        <button
          onClick={deleteModels}
          disabled={isTesting || isDeleting || isDeletingStandard}
          className="pressable rounded-full bg-bg-card2 px-4 py-2 text-xs font-bold text-ink-secondary ring-1 ring-stroke disabled:opacity-50"
        >
          {isDeleting ? 'Deleting...' : 'Delete Models'}
        </button>
      </div>

      {testingModel ? (
        <p className="mt-3 rounded-xl bg-bg-card2 px-3 py-2 text-xs leading-relaxed text-ink-secondary ring-1 ring-stroke/70">
          {testingModel === 'enhanced'
            ? 'Testing E4B can take several minutes while the model downloads, loads, warms up, and generates.'
            : 'Testing E2B can take a while on the first run while LiteRT-LM builds its cache.'}
        </p>
      ) : null}

      {capability?.hardGate ? (
        <p className="mt-3 rounded-xl bg-f1-red/10 px-3 py-2 text-xs leading-relaxed text-ink-secondary ring-1 ring-f1-red/20">
          Gate: {capability.hardGate}. The app will use online AI unless this changes after a retest.
        </p>
      ) : null}

      {capability?.enhancedReason ? (
        <p className="mt-3 rounded-xl bg-bg-card2 px-3 py-2 text-xs leading-relaxed text-ink-secondary ring-1 ring-stroke/70">
          E4B: {capability.enhancedReason}
          {capability.enhancedHardGate ? ` Gate: ${capability.enhancedHardGate}.` : ''}
        </p>
      ) : null}

      {standardModelDeleted ? (
        <p className="mt-3 rounded-xl bg-bg-card2 px-3 py-2 text-xs leading-relaxed text-ink-secondary ring-1 ring-stroke/70">
          E2B has been removed. Oracle will use E4B when local AI is selected; retesting E2B will download it again.
        </p>
      ) : null}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl bg-bg-card2 px-3 py-2 ring-1 ring-stroke/70">
      <p className="text-[10px] font-bold uppercase tracking-widest-plus text-ink-tertiary">{label}</p>
      <p className="mt-1 truncate text-xs font-semibold text-ink-primary">{value}</p>
    </div>
  );
}

function PreferenceButton({ label, description, active, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`pressable w-full rounded-2xl px-3 py-2 text-left ring-1 transition-all disabled:opacity-45 ${
        active
          ? 'bg-f1-red/15 ring-f1-red/60'
          : 'bg-bg-card2 ring-stroke hover:ring-f1-red/35'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-bold text-ink-primary">{label}</span>
        <span className={`h-2 w-2 rounded-full ${active ? 'bg-f1-red' : 'bg-ink-muted'}`} />
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-ink-secondary">{description}</p>
    </button>
  );
}

function scoreColour(score) {
  if (typeof score !== 'number') return 'bg-bg-card text-ink-tertiary ring-1 ring-stroke';
  if (score >= 80) return 'bg-signal-green/15 text-signal-green ring-1 ring-signal-green/30';
  if (score >= 60) return 'bg-f1-red/15 text-f1-red ring-1 ring-f1-red/30';
  return 'bg-bg-card text-ink-tertiary ring-1 ring-stroke';
}

function formatTier(tier) {
  switch (tier) {
    case LOCAL_AI_TIERS.enhanced:
      return 'Enhanced';
    case LOCAL_AI_TIERS.enhancedCandidate:
      return 'E4B candidate';
    case LOCAL_AI_TIERS.standard:
      return 'Standard';
    case LOCAL_AI_TIERS.onlineOnly:
      return 'Online only';
    default:
      return 'Not tested';
  }
}

function formatSpeed(value) {
  if (typeof value !== 'number') return '--';
  return `${value.toFixed(1)} tok/s`;
}

function formatMs(value) {
  if (typeof value !== 'number') return '--';
  return `${Math.round(value)} ms`;
}

function waitForPaint() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    } else {
      setTimeout(resolve, 0);
    }
  });
}
